import React, { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Cell, LabelList
} from "recharts";
import { formatCurrency } from "../utils/formatCurrency";
import api from "../api";

const COLORS = [
  "#1976d2", "#43a047", "#e53935", "#fb8c00", "#8e24aa",
  "#00acc1", "#f4511e", "#6d4c41", "#3949ab", "#00897b",
  "#c0ca33", "#5e35b1"
];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8,
      padding: "10px 14px", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", fontSize: 13
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ color: "#555" }}>{formatCurrency(payload[0].value)}</div>
      <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>Cliquer pour voir les transactions</div>
    </div>
  );
}

export default function ExpenseSubcategoryChart({ data, category, month }) {
  const [selectedBar, setSelectedBar] = useState(null); // { name, subcategory_id, colorIdx }
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);

  if (!category) return null;

  const filtered = (data || [])
    .filter(d =>
      (d.category_name || "").toLowerCase().trim() ===
      (category || "").toLowerCase().trim()
    )
    .map(d => ({
      name: d.subcategory_name,
      subcategory_id: d.subcategory_id,
      amount: Number(d.spent)
    }))
    .filter(d => d.amount > 0)
    .sort((a, b) => b.amount - a.amount);

  if (filtered.length === 0) {
    return (
      <div style={{ marginTop: 32, padding: "16px 20px", background: "#f5f5f5", borderRadius: 10 }}>
        <h3 style={{ margin: 0 }}>Détail : {category}</h3>
        <p style={{ color: "#888", marginTop: 8 }}>Aucune dépense dans cette catégorie.</p>
      </div>
    );
  }

  const maxVal = Math.max(...filtered.map(d => d.amount));

  async function handleBarClick(entry, index) {
    const isSame = selectedBar?.subcategory_id === entry.subcategory_id;
    if (isSame) { setSelectedBar(null); setTransactions([]); return; }

    setSelectedBar({ ...entry, colorIdx: index });
    setLoadingTx(true);
    try {
      const params = new URLSearchParams({ subcategory_id: entry.subcategory_id });
      if (month) {
        const [y, m] = month.split("-");
        params.set("date_from", `${y}-${m}-01`);
        const lastDay = new Date(Number(y), Number(m), 0).getDate();
        params.set("date_to", `${y}-${m}-${String(lastDay).padStart(2, "0")}`);
      }
      const res = await api.get(`/transactions?${params}`);
      const rows = Array.isArray(res.data) ? res.data : res.data?.transactions || [];
      setTransactions(rows.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (e) { console.error(e); setTransactions([]); }
    finally { setLoadingTx(false); }
  }

  const selectedColor = selectedBar ? COLORS[selectedBar.colorIdx % COLORS.length] : null;

  return (
    <div style={{ padding: "20px 24px", background: "#fafafa", borderRadius: 12, border: "1px solid #ebebeb" }}>
      <h3 style={{ margin: "0 0 20px", fontSize: 16 }}>
        Détail : <span style={{ color: "#1976d2" }}>{category}</span>
      </h3>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={filtered}
          margin={{ top: 16, right: 16, left: 0, bottom: 70 }}
          onClick={({ activePayload, activeTooltipIndex }) => {
            if (activePayload?.[0]) handleBarClick(activePayload[0].payload, activeTooltipIndex ?? 0);
          }}
          style={{ cursor: "pointer" }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#ececec" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fill: "#555", angle: -40, textAnchor: "end", dy: 6 }}
            axisLine={false} tickLine={false} interval={0}
          />
          <YAxis
            tickFormatter={v => formatCurrency(v)}
            tick={{ fontSize: 11, fill: "#999" }}
            axisLine={false} tickLine={false}
            domain={[0, Math.ceil(maxVal * 1.15)]} width={70}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
          <Bar dataKey="amount" radius={[6, 6, 0, 0]} maxBarSize={80}>
            {filtered.map((entry, index) => (
              <Cell
                key={index}
                fill={COLORS[index % COLORS.length]}
                opacity={selectedBar && selectedBar.subcategory_id !== entry.subcategory_id ? 0.35 : 1}
                stroke={selectedBar?.subcategory_id === entry.subcategory_id ? "#111" : "none"}
                strokeWidth={2}
              />
            ))}
            <LabelList
              dataKey="amount"
              position="top"
              formatter={v => formatCurrency(v)}
              style={{ fontSize: 11, fill: "#555", fontWeight: 600 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* ── Liste des transactions ──────────────────────────────── */}
      {selectedBar && (
        <div style={{ marginTop: 20, borderTop: `3px solid ${selectedColor}`, paddingTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: selectedColor }}>
              {selectedBar.name}
            </span>
            <button
              onClick={() => { setSelectedBar(null); setTransactions([]); }}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#999", lineHeight: 1 }}
            >✕</button>
          </div>

          {loadingTx ? (
            <div style={{ color: "#999", fontSize: 13 }}>Chargement…</div>
          ) : transactions.length === 0 ? (
            <div style={{ color: "#999", fontSize: 13 }}>Aucune transaction trouvée.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e0e0e0" }}>
                  <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 600, color: "#888", fontSize: 11, textTransform: "uppercase" }}>Date</th>
                  <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 600, color: "#888", fontSize: 11, textTransform: "uppercase" }}>Marchand / Libellé</th>
                  <th style={{ textAlign: "right", padding: "4px 8px", fontWeight: 600, color: "#888", fontSize: 11, textTransform: "uppercase" }}>Montant</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(tx => (
                  <tr key={tx.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "6px 8px", color: "#888", whiteSpace: "nowrap" }}>
                      {new Date(tx.budget_date || tx.date).toLocaleDateString("fr-FR")}
                    </td>
                    <td style={{ padding: "6px 8px", fontWeight: 500 }}>
                      {tx.merchant || tx.raw_label || "—"}
                    </td>
                    <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 600, color: tx.type === "income" ? "var(--success)" : "var(--text)" }}>
                      {tx.type === "income" ? "+" : ""}{formatCurrency(Math.abs(tx.amount))}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: "2px solid #e0e0e0" }}>
                  <td colSpan={2} style={{ padding: "6px 8px", fontWeight: 600, fontSize: 13 }}>Total</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700, color: selectedColor }}>
                    {formatCurrency(selectedBar.amount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
